/**
 * 최종산출물(/preview) 쿼리 플랜 — **단일 출처**.
 *
 * FinalPreview 트리가 렌더에 쓰는 쿼리를 두 가지로 다룬다:
 *  - gatherPreviewData: (버튼) fetchQuery 로 응답을 모아 임베드 데이터로 직렬화
 *  - seedPreviewCache:  (뷰어) 같은 쿼리 키로 오프라인 캐시에 setQueryData
 *
 * 둘을 같은 파일에 둬, 쿼리 키 ↔ 데이터 필드 매핑이 흩어져 drift 나는 걸 막는다.
 * 콘텐츠 패널(쿼리)을 추가/변경할 땐 이 파일의 gather/seed *한 쌍*만 같이 고치면 된다.
 * (cascade: notUsedSumTagKeys → tablePreviewV2 는 gather 단계에서만 의미가 있다.)
 */

import type { QueryClient } from "@tanstack/react-query";

import queries from "@/queries";
import type { FinalPreviewExportData, FinalPreviewTableData } from "./types";

// 이미 라이브 화면에서 대부분 캐시된 응답이라 fetchQuery 는 즉시 반환되는 경우가 많다.
const FETCH_OPTS = {
  staleTime: 10 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
} as const;

/** 임베드 데이터에서 export 직전에 채우는 필드(이미지 등)를 뺀 "쿼리로 모으는" 부분. */
export type GatheredPreviewData = Omit<FinalPreviewExportData, "imageAssets">;

async function gatherTable(
  queryClient: QueryClient,
  workbookId: string,
  tableId: string,
): Promise<FinalPreviewTableData> {
  const tbl = queries.workbook(workbookId)._ctx.tables(tableId);
  const [dsdView, treeView, notUsedSumTagKeys] = await Promise.all([
    queryClient.fetchQuery({ ...tbl._ctx.dsdView(), ...FETCH_OPTS }),
    queryClient.fetchQuery({
      ...tbl._ctx.tableTreeViewV2(),
      ...FETCH_OPTS,
    }),
    queryClient.fetchQuery({ ...tbl._ctx.notUsedSumTagKeys(), ...FETCH_OPTS }),
  ]);
  // cascade: 위에서 받은 notUsedSumTagKeys 를 파라미터로 사용.
  const tablePreview = await queryClient.fetchQuery({
    ...tbl._ctx
      .editablePreview()
      ._ctx.withQueryParam(notUsedSumTagKeys.notUsedSumTagKeys),
    ...FETCH_OPTS,
  });
  return { tableId, dsdView, treeView, notUsedSumTagKeys, tablePreview };
}

/** (버튼) 라이브 트리가 렌더에 쓰는 모든 쿼리를 확보해 직렬화 가능한 형태로 반환. */
export async function gatherPreviewData(
  queryClient: QueryClient,
  workbookId: string,
): Promise<GatheredPreviewData> {
  const wb = queries.workbook(workbookId);
  const [categories, taggingStatus, dsdFiles] = await Promise.all([
    queryClient.fetchQuery({ ...wb._ctx.categories(), ...FETCH_OPTS }),
    queryClient.fetchQuery({ ...wb._ctx.tablesTaggingStatus(), ...FETCH_OPTS }),
    queryClient.fetchQuery({ ...queries.dsd(), ...FETCH_OPTS }),
  ]);
  const tableIds = categories.categories.flatMap((category) =>
    category.tables.map((table) => table.id),
  );
  const tables = await Promise.all(
    tableIds.map((tableId) => gatherTable(queryClient, workbookId, tableId)),
  );
  return { workbookId, categories, taggingStatus, dsdFiles, tables };
}

function seedTable(
  queryClient: QueryClient,
  workbookId: string,
  t: FinalPreviewTableData,
): void {
  const tbl = queries.workbook(workbookId)._ctx.tables(t.tableId);
  queryClient.setQueryData(tbl._ctx.dsdView().queryKey, t.dsdView);
  queryClient.setQueryData(tbl._ctx.tableTreeViewV2().queryKey, t.treeView);
  queryClient.setQueryData(
    tbl._ctx.notUsedSumTagKeys().queryKey,
    t.notUsedSumTagKeys,
  );
  queryClient.setQueryData(
    tbl._ctx.editablePreview()._ctx.withQueryParam([]).queryKey,
    t.tablePreview,
  );
}

/** (뷰어) 임베드 데이터를 라이브 트리가 호출하는 쿼리 키 그대로 캐시에 심는다. */
export function seedPreviewCache(
  queryClient: QueryClient,
  data: FinalPreviewExportData,
): void {
  const wb = queries.workbook(data.workbookId);
  queryClient.setQueryData(wb._ctx.categories().queryKey, data.categories);
  queryClient.setQueryData(
    wb._ctx.tablesTaggingStatus().queryKey,
    data.taggingStatus,
  );
  queryClient.setQueryData(queries.dsd().queryKey, data.dsdFiles);
  for (const t of data.tables) seedTable(queryClient, data.workbookId, t);
}
